import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Stack, Typography } from '@emburse/embark-core';
import { useDialog } from '../../components/Dialog';
import convertMillisecondsToTimeRemaining from './util';
function ModalBody({ endSessionTimer }) {
    const timeRemaining = convertMillisecondsToTimeRemaining(endSessionTimer);
    return (_jsx(Stack, { alignItems: "flex-start", children: _jsx(Typography, { children: `You've been inactive for a while. To keep your account secure, you'll be automatically logged out in ${timeRemaining}.
           If you're still here, click Keep Working to stay logged in.` }) }));
}
export default function SessionExpirationOverlay({ warningBufferOffset, initialTokenCallback, refreshTokenCallback, logoutCallback, sessionRefreshOnInteraction, children }) {
    const { enqueueDialog } = useDialog();
    const modalTimeout = useRef(null);
    const expirationTimeout = useRef(null);
    const [sessionExpirationDatetime, setSessionExpirationDatetime] = useState(null);
    const [userInteracted, setUserInteracted] = useState(false);
    const setupUserInteractionEvent = useCallback(() => {
        const updateUserInteractionTimestamp = () => {
            setUserInteracted(() => true);
        };
        window.addEventListener('click', updateUserInteractionTimestamp);
        window.addEventListener('keydown', updateUserInteractionTimestamp);
        window.addEventListener('wheel', updateUserInteractionTimestamp);
        return () => {
            window.removeEventListener('click', updateUserInteractionTimestamp);
            window.removeEventListener('keydown', updateUserInteractionTimestamp);
            window.removeEventListener('wheel', updateUserInteractionTimestamp);
        };
    }, []);
    const sessionExpirationDialog = useMemo(() => ({
        title: 'Are you still working?',
        show: true,
        showClose: false,
        hideBackdrop: false,
        confirmText: 'Keep Working',
        content: _jsx(ModalBody, { endSessionTimer: warningBufferOffset }),
        onConfirm: () => {
            (async () => {
                const newExpiryDate = await refreshTokenCallback();
                setSessionExpirationDatetime(newExpiryDate);
            })();
        },
        onClose: (_event, reason) => {
            (async () => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    const newExpiryDate = await refreshTokenCallback();
                    setSessionExpirationDatetime(newExpiryDate);
                }
                else {
                    logoutCallback();
                }
            })();
        }
    }), [logoutCallback, refreshTokenCallback, warningBufferOffset]);
    const modalTimeoutCallback = useCallback(async () => {
        if (userInteracted) {
            const newExpiryDate = await refreshTokenCallback();
            setSessionExpirationDatetime(newExpiryDate);
        }
        else {
            enqueueDialog(sessionExpirationDialog);
        }
    }, [enqueueDialog, refreshTokenCallback, sessionExpirationDialog, userInteracted]);
    useMemo(() => {
        if (sessionExpirationDatetime === null) {
            return;
        }
        clearTimeout(modalTimeout.current);
        clearTimeout(expirationTimeout.current);
        const sessionExpirationUnixTime = sessionExpirationDatetime.getTime();
        const warningModalTimeout = sessionExpirationUnixTime - Date.now() - warningBufferOffset;
        const terminateSessionTimeout = sessionExpirationUnixTime - Date.now();
        modalTimeout.current = setTimeout(() => modalTimeoutCallback(), Math.max(warningModalTimeout, 0));
        expirationTimeout.current = setTimeout(() => logoutCallback(), Math.max(terminateSessionTimeout, 0));
    }, [logoutCallback, modalTimeoutCallback, sessionExpirationDatetime, warningBufferOffset]);
    useMemo(async () => {
        const initialTokenExpiration = await initialTokenCallback();
        setSessionExpirationDatetime(initialTokenExpiration);
        if (sessionRefreshOnInteraction) {
            return setupUserInteractionEvent();
        }
        return () => { };
    }, [initialTokenCallback, sessionRefreshOnInteraction, setupUserInteractionEvent]);
    return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    _jsx(_Fragment, { children: children }));
}
